import { useState, useEffect, useRef } from 'react';
import { submitCaFormResponse, uploadImage, uploadFile } from '../utils/api';
import { toast } from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';

export default function RequestModal({
  formId,
  title,
  fields,
  options,
  thankYouMessage,
  onClose,
  onSubmitted,
  userProfile // Add this prop
}) {
  const [formValues, setFormValues] = useState(
    fields.reduce((acc, f) => ({
      ...acc,
      [f._id]:
        f.type === 'checkbox-group' || f.type === 'file-multiple'
          ? []
          : f.type === 'checkbox'
            ? false
            : '',
    }), {}),
  );
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  const modalRef = useRef(null);

  useEffect(() => {
    if (userProfile) {
      setFormValues(prev => ({
        ...prev,
        userEmail: userProfile.email || '',
        userPhone: userProfile.phone || ''
      }));
    }
  }, [userProfile]);

  const handleChange = (fieldId, value) => {
    setFormValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleFileChange = async (field, fileInput) => {
    const { _id: fieldId, type } = field;
    if (!fileInput) return;
    setUploading(prev => ({ ...prev, [fieldId]: true }));

    try {
      if (type === 'image') {
        const { url } = await uploadImage(fileInput);
        setFormValues(prev => ({ ...prev, [fieldId]: url }));
      } else if (type === 'file') {
        const { url } = await uploadFile(fileInput);
        setFormValues(prev => ({ ...prev, [fieldId]: url }));
      } else if (type === 'file-multiple') {
        const files = Array.from(fileInput);
        const urls = [];
        for (const file of files) {
          const { url } = await uploadFile(file);
          urls.push(url);
        }
        setFormValues(prev => ({ ...prev, [fieldId]: urls }));
      }
    } catch (err) {
      console.error('Upload failed for field', fieldId, err);
      toast.error('Upload failed for ' + field.name);
    } finally {
      setUploading(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const validateForm = () => {
    for (const field of fields) {
      const val = formValues[field._id];
      if (field.required) {
        if (
          val === undefined ||
          val === null ||
          val === '' ||
          (Array.isArray(val) && val.length === 0) ||
          (typeof val === 'boolean' && val === false)
        ) {
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill all required fields.');
      return;
    }

    setSubmitting(true);
    try {
      // Include user contact info in the submission
      const submissionData = {
        ...formValues,
        userEmail: userProfile?.email,
        userPhone: userProfile?.phone
      };

      await submitCaFormResponse(formId, submissionData);
      toast.success(thankYouMessage || 'Form submitted successfully');
      onClose();
      onSubmitted?.();
    } catch (err) {
      console.error(err);
      toast.error('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white p-6 rounded-lg w-full max-w-md overflow-y-auto max-h-screen"
      >
        <h2 className="text-xl font-semibold mb-4">{title}</h2>

        {/* Display user contact info */}
        {userProfile && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Your Contact Information</h3>
            <div className="text-sm text-gray-600">
              <p>Email: {userProfile.email}</p>
              <p>Phone: {userProfile.phone}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map(field => {
            const { _id: fieldId, name, type, required } = field;
            const val = formValues[fieldId];
            const isUploading = uploading[fieldId];

            const commonProps = {
              id: fieldId,
              name: fieldId,
              required,
            };

            switch (type) {
              case 'text':
              case 'email':
              case 'url':
              case 'tel':
              case 'password':
              case 'number':
                return (
                  <div key={fieldId} className="flex flex-col">
                    <label htmlFor={fieldId} className="mb-1 font-medium text-gray-700">
                      {name}
                      {required && <span className="text-red-500"> *</span>}
                    </label>
                    <input
                      {...commonProps}
                      type={type}
                      value={val}
                      onChange={e => handleChange(fieldId, e.target.value)}
                      className="w-full border px-3 py-2 rounded"
                    />
                  </div>
                );

              case 'textarea':
                return (
                  <div key={fieldId} className="flex flex-col">
                    <label htmlFor={fieldId} className="mb-1 font-medium text-gray-700">
                      {name}
                      {required && <span className="text-red-500"> *</span>}
                    </label>
                    <textarea
                      {...commonProps}
                      value={val}
                      onChange={e => handleChange(fieldId, e.target.value)}
                      className="w-full border px-3 py-2 rounded"
                      rows={4}
                    />
                  </div>
                );

              case 'checkbox':
                return (
                  <div key={fieldId} className="flex items-center">
                    <input
                      {...commonProps}
                      type="checkbox"
                      checked={val}
                      onChange={e => handleChange(fieldId, e.target.checked)}
                    />
                    <label htmlFor={fieldId} className="ml-2">{name}</label>
                  </div>
                );

              case 'checkbox-group':
                return (
                  <div key={fieldId} className="space-y-2">
                    <span className="font-medium text-gray-700">{name}</span>
                    {options
                      .filter(o => o.form_field_id === fieldId)
                      .map(opt => (
                        <label key={opt._id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={val.includes(opt.value)}
                            onChange={e => {
                              const arr = val;
                              handleChange(
                                fieldId,
                                e.target.checked
                                  ? [...arr, opt.value]
                                  : arr.filter(v => v !== opt.value),
                              );
                            }}
                          />
                          <span>{opt.value}</span>
                        </label>
                      ))}
                  </div>
                );

              case 'radio':
                return (
                  <div key={fieldId} className="space-y-2">
                    <span className="font-medium text-gray-700">{name}</span>
                    {options
                      .filter(o => o.form_field_id === fieldId)
                      .map(opt => (
                        <label key={opt._id} className="flex items-center space-x-2">
                          <input
                            type="radio"
                            {...commonProps}
                            checked={val === opt.value}
                            onChange={() => handleChange(fieldId, opt.value)}
                          />
                          <span>{opt.value}</span>
                        </label>
                      ))}
                  </div>
                );

              case 'select':
                return (
                  <div key={fieldId} className="flex flex-col">
                    <label htmlFor={fieldId} className="mb-1 font-medium text-gray-700">
                      {name}
                    </label>
                    <select
                      {...commonProps}
                      value={val}
                      onChange={e => handleChange(fieldId, e.target.value)}
                      className="w-full border px-3 py-2 rounded"
                    >
                      <option value="">Select…</option>
                      {options
                        .filter(o => o.form_field_id === fieldId)
                        .map(opt => (
                          <option key={opt._id} value={opt.value}>
                            {opt.value}
                          </option>
                        ))}
                    </select>
                  </div>
                );

              case 'date':
              case 'time':
              case 'datetime':
              case 'range':
                return (
                  <div key={fieldId} className="flex flex-col">
                    <label htmlFor={fieldId} className="mb-1 font-medium text-gray-700">
                      {name}
                    </label>
                    <input
                      {...commonProps}
                      type={type === 'datetime' ? 'datetime-local' : type}
                      value={val}
                      onChange={e => handleChange(fieldId, e.target.value)}
                      className="w-full border px-3 py-2 rounded"
                    />
                  </div>
                );

              case 'image':
              case 'file':
              case 'file-multiple':
                return (
                  <div key={fieldId} className="flex flex-col">
                    <label className="mb-1 font-medium text-gray-700">{name}</label>
                    <input
                      type="file"
                      multiple={type === 'file-multiple'}
                      onChange={e => handleFileChange(field, type === 'file-multiple' ? e.target.files : e.target.files[0])}
                    />
                    {isUploading && <span className="text-sm text-gray-500">Uploading…</span>}
                    {type !== 'file-multiple' && val && <a href={val} className="text-blue-600 text-sm">View uploaded</a>}
                    {type === 'file-multiple' && val.length > 0 && (
                      <ul className="list-disc ml-5 text-sm text-blue-600">
                        {val.map((url, i) => (
                          <li key={i}><a href={url}>File {i + 1}</a></li>
                        ))}
                      </ul>
                    )}
                  </div>
                );

              default:
                return null;
            }
          })}

          <div className="flex justify-end space-x-2 pt-4">
            <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 border rounded">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !validateForm()}
              className="px-4 py-2 bg-black text-white rounded flex items-center justify-center min-w-[120px]"
            >
              {submitting ? <ClipLoader color="#ffffff" loading /> : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
